import React from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Eye, FileText, Volume2, Image } from 'lucide-react';

interface MediaAnalysis {
  detected_content?: Array<{
    type: string;
    description: string;
    confidence: number;
  }>;
  extracted_text?: string;
  document_summary?: string;
  audio_transcription?: string;
  visual_elements?: Array<{
    element_type: string;
    description: string;
    text_content?: string;
    confidence: number;
  }>;
}

interface AIMediaInsightsProps {
  mediaType: 'image' | 'document' | 'audio' | 'video' | 'sticker';
  mediaUrl?: string;
  aiMetadata?: {
    visionModelUsed?: boolean;
    mediaProcessed?: boolean;
    media_analysis?: MediaAnalysis;
  };
}

export const AIMediaInsights: React.FC<AIMediaInsightsProps> = ({
  mediaType,
  mediaUrl,
  aiMetadata
}) => {
  const { t } = useTranslation('conversations');
  const { visionModelUsed, mediaProcessed, media_analysis } = aiMetadata || {};

  if (!mediaProcessed || !media_analysis) {
    return null;
  }

  const getMediaIcon = () => {
    switch (mediaType) {
      case 'image':
      case 'sticker':
        return <Image className="w-4 h-4" />;
      case 'document':
        return <FileText className="w-4 h-4" />;
      case 'audio':
        return <Volume2 className="w-4 h-4" />;
      default:
        return <Eye className="w-4 h-4" />;
    }
  };

  const hasContent = Boolean(
    media_analysis.detected_content?.length ||
    media_analysis.extracted_text ||
    media_analysis.document_summary ||
    media_analysis.audio_transcription ||
    media_analysis.visual_elements?.length
  );

  if (!hasContent) {
    return null;
  }

  return (
    <Card className="mt-2 border-primary/20 bg-primary/5">
      <CardContent className="p-3 space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-primary">
          {getMediaIcon()}
          <span>{t('mediaInsights.title')}</span>
          {visionModelUsed && (
            <Badge variant="secondary" className="text-xs">
              Vision Model
            </Badge>
          )}
        </div>

        {/* Detected Content */}
        {media_analysis.detected_content && media_analysis.detected_content.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">{t('mediaInsights.objects')}</h4>
            <div className="flex flex-wrap gap-1">
              {media_analysis.detected_content.map((item, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="text-xs"
                  title={`Confidence: ${(item.confidence * 100).toFixed(1)}%`}
                >
                  {item.description}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Extracted Text */}
        {media_analysis.extracted_text && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">{t('mediaInsights.text')}</h4>
            <div className="text-sm bg-muted/50 p-2 rounded text-foreground/80">
              {media_analysis.extracted_text}
            </div>
          </div>
        )}

        {/* Document Summary */}
        {media_analysis.document_summary && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">{t('mediaInsights.documentSummary')}</h4>
            <div className="text-sm bg-muted/50 p-2 rounded text-foreground/80">
              {media_analysis.document_summary}
            </div>
          </div>
        )}

        {/* Audio Transcription */}
        {media_analysis.audio_transcription && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">{t('mediaInsights.audioTranscript')}</h4>
            <div className="text-sm bg-muted/50 p-2 rounded text-foreground/80">
              {media_analysis.audio_transcription}
            </div>
          </div>
        )}

        {/* Visual Elements */}
        {media_analysis.visual_elements && media_analysis.visual_elements.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">{t('mediaInsights.visualElements')}</h4>
            <div className="space-y-1">
              {media_analysis.visual_elements.map((element, index) => (
                <div key={index} className="text-sm">
                  <Badge variant="outline" className="text-xs mr-2">
                    {element.element_type}
                  </Badge>
                  <span className="text-foreground/80">{element.description}</span>
                  {element.text_content && (
                    <div className="ml-4 mt-1 text-xs bg-muted/30 p-1 rounded">
                      "{element.text_content}"
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};